import { useEffect, useState } from 'react';
import Script from 'next/script';

import CustomMetaData, {
  CustomMetaDataProps,
} from 'components/general/customMetaData/CustomMetaData';
import GenericPageLayout, {
  GenericPageLayoutProps,
} from 'components/general/genericPageLayout/GenericPageLayout';
import { useGlobalContentContext } from 'context/GlobalContentContext';
import { NEXTJS_REVALIDATE_PAGE_IN_SECONDS } from 'pages/index';
import { parseGenericPage } from 'utils/parsers';

import style from './indexPrivacy.module.scss';

type Props = {
  generic: GenericPageLayoutProps;
  title: string;
  text: string;
  metaData: CustomMetaDataProps;
};

const Privacy = (props: Props) => {
  const { generic, text } = props;
  const [OneTrustAPI, setOneTrustAPI] = useState(undefined);

  const { setData } = useGlobalContentContext();
  useEffect(() => {
    setData(props.generic.globalData);
  }, []);

  const conditionallyLoadOneTrust = () => {
    // @ts-ignore
    if (window.OneTrust && window.OneTrust !== OneTrustAPI && window.OneTrust.NoticeApi) {
      // @ts-ignore
      setOneTrustAPI(window.OneTrust);
      // @ts-ignore
      window.OneTrust.NoticeApi.Initialized.then(function () {
        // @ts-ignore
        window.OneTrust.NoticeApi.LoadNotices([
          'https://privacyportal-cdn.onetrust.com/storage-container/73dca12b-5ba4-4937-9072-b5ffa15d1ba7/privacy-notices/b3901045-3dea-42ef-9541-a469458052a6/published/privacynotice.json'
        ]);
      });
    }
  };

  // This awkward approach was the most reliable for making sure the externally sourced content was always loaded
  // in lieu of Next/Script's onReady callback which isn't available in this version of Next.
  useEffect(conditionallyLoadOneTrust); // NOTE: intentionally no dependency array here

  return (
    <>
      <Script
        src="https://privacyportal-cdn.onetrust.com/privacy-notice-scripts/otnotice-1.0.min.js"
        type="text/javascript"
        id="otprivacy-notice-script"
        onLoad={conditionallyLoadOneTrust}
      >
        settings=&#34;eyJjYWxsYmFja1VybCI6Imh0dHBzOi8vcHJpdmFjeXBvcnRhbC5vbmV0cnVzdC5jb20vcmVxdWVzdC92MS9wcml2YWN5Tm90aWNlcy9zdGF0cy92aWV3cyIsImNvbnRlbnRBcGlVcmwiOiJodHRwczovL3ByaXZhY3lwb3J0YWwub25ldHJ1c3QuY29tL3JlcXVlc3QvdjEvZW50ZXJwcmlzZXBvbGljeS9kaWdpdGFscG9saWN5L2NvbnRlbnQiLCJtZXRhZGF0YUFwaVVybCI6Imh0dHBzOi8vcHJpdmFjeXBvcnRhbC5vbmV0cnVzdC5jb20vcmVxdWVzdC92MS9lbnRlcnByaXNlcG9saWN5L2RpZ2l0YWxwb2xpY3kvbWV0YS1kYXRhIn0=&#34;
      </Script>
      <GenericPageLayout {...generic} blocks={[]}>
        <CustomMetaData {...props.metaData} />
        <div className={style.grid}>
          <div
            id="otnotice-b3901045-3dea-42ef-9541-a469458052a6"
            className={`otnotice ${style.text}`}
          ></div>
        </div>
      </GenericPageLayout>
    </>
  );
};

export async function getStaticProps(context) {
  const genericProps: GenericPageLayoutProps = await parseGenericPage(`/privacy-policy/`, context);
  genericProps.blocks = [];
  const props: Props = {
    generic: genericProps,
    title: genericProps.originalResponse.title,
    text: genericProps.originalResponse.text,
    metaData: {
      title: genericProps.originalResponse.meta_title,
      description: genericProps.originalResponse.meta_description,
      image: genericProps.originalResponse.meta_image,
    },
  };
  // Pass data to the page via props
  return {
    props: props,
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 10 seconds
    revalidate: NEXTJS_REVALIDATE_PAGE_IN_SECONDS, // In seconds
  };
}

export default Privacy;
